import { useMediaQuery } from 'react-responsive';

import { useTranslation } from '@lichtblick/i18next';
import { Colors, TextVariants } from '@lichtblick/theme';
import { GetWorkOrderAppointmentsPayloadModel } from '@lichtblick/types';
import { BREAKPOINTS } from '@lichtblick/utils';
import { createSchedulingCalendarUrl } from '@lichtblick/utils/src/urls';

import {
  ContainerHeader,
  Table,
  MobileContainer,
  MobileCard,
  MobileCardHeader,
  MobileCardContent,
  MobileInfoRow,
  MobileLabel,
  MobileValue,
} from './WorkOrderAppointmentsTable.styles';

import { Appointment } from '../Appointment';
import { Text } from '../Text';
import { WorkOrderAppointmentItem } from '../WorkOrderAppointmentItem/WorkOrderAppointmentItem';

type WorkOrderAppointmentsTableProps = {
  appointments: GetWorkOrderAppointmentsPayloadModel[];
};

export const WorkOrderAppointmentsTable = ({ appointments }: WorkOrderAppointmentsTableProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.MOBILE });

  const onAppointmentNavigate = (appointment: GetWorkOrderAppointmentsPayloadModel) => {
    const url = createSchedulingCalendarUrl(appointment.id);

    window.open(url, '_blank');
  };

  if (isMobile) {
    return (
      <MobileContainer>
        {appointments?.map((appointment) => {
          const partners = appointment.partnerCompanies?.map((comp) => comp.name) ?? [];
          const users = appointment.craftsmen?.map((user) => `${user.firstName} ${user.lastName}`) ?? [];
          const members = [...partners, ...users];

          return (
            <MobileCard key={appointment.id} onClick={() => onAppointmentNavigate(appointment)}>
              <MobileCardHeader>
                <Appointment
                  endDate={appointment.endDateUtc}
                  isBinding={appointment.isBinding}
                  startDate={appointment.startDateUtc}
                  variant={appointment.type ?? 'B'}
                />
              </MobileCardHeader>
              <MobileCardContent>
                {appointment.branchOffice?.name && (
                  <MobileInfoRow>
                    <MobileLabel>
                      <Text $variant={TextVariants.body.s} color={Colors.gray['50']}>
                        {t('pm:branch')}:
                      </Text>
                    </MobileLabel>
                    <MobileValue>
                      <Text $variant={TextVariants.body.m}>{appointment.branchOffice.name}</Text>
                    </MobileValue>
                  </MobileInfoRow>
                )}
                {appointment.comment && (
                  <MobileInfoRow>
                    <MobileLabel>
                      <Text $variant={TextVariants.body.s} color={Colors.gray['50']}>
                        {t('pm:notes')}:
                      </Text>
                    </MobileLabel>
                    <MobileValue>
                      <Text $variant={TextVariants.body.m}>{appointment.comment}</Text>
                    </MobileValue>
                  </MobileInfoRow>
                )}
                {members.length > 0 && (
                  <MobileInfoRow>
                    <MobileLabel>
                      <Text $variant={TextVariants.body.s} color={Colors.gray['50']}>
                        {t('pm:members')}:
                      </Text>
                    </MobileLabel>
                    <MobileValue>
                      <Text $variant={TextVariants.body.m}>{members.join(', ')}</Text>
                    </MobileValue>
                  </MobileInfoRow>
                )}
              </MobileCardContent>
            </MobileCard>
          );
        })}
      </MobileContainer>
    );
  }

  return (
    <Table>
      <ContainerHeader>
        <tr>
          <th>
            <Text $variant={TextVariants.body.m} color={Colors.gray['50']}>
              {t('pm:appointment')}
            </Text>
          </th>
          <th>
            <Text $variant={TextVariants.body.m} color={Colors.gray['50']}>
              {t('pm:branch')}
            </Text>
          </th>
          <th>
            <Text $variant={TextVariants.body.m} color={Colors.gray['50']}>
              {t('pm:notes')}
            </Text>
          </th>
          <th>
            <Text $variant={TextVariants.body.m} color={Colors.gray['50']}>
              {t('pm:members')}
            </Text>
          </th>
          <th />
        </tr>
      </ContainerHeader>
      <tbody>
        {appointments?.map((appointment) => (
          <WorkOrderAppointmentItem
            appointment={appointment}
            key={appointment.id}
            onNavigate={() => onAppointmentNavigate(appointment)}
          />
        ))}
      </tbody>
    </Table>
  );
};
